import * as React from "react";
import type { ComponentRenderProps } from "@json-render/react";
import { Skeleton } from "@erp/ui/skeleton";
import { APP_COMPONENTS } from "../catalog";
import { readProps } from "../props";
import { AppThemeCssContext } from "./scope";
import type { ChartRequest } from "./chartsImpl";

/**
 * Chart components. The Vega engine (`@erp/viz`, hundreds of KB) lives in
 * `./chartsImpl`, loaded on first use, so a view without charts never pays
 * for it. A skeleton holds the chart's height while it loads.
 */

const ChartView = React.lazy(() => import("./chartsImpl"));

const DEFAULT_HEIGHT = 280;

function ChartHost({ request }: { request: ChartRequest }) {
  // The chart reads its theme tokens from this element, so it follows the
  // App's scope; it mounts once the element exists.
  const [root, setRoot] = React.useState<HTMLElement | null>(null);
  const themeCss = React.use(AppThemeCssContext);
  const height = request.props.height ?? DEFAULT_HEIGHT;
  // Props are rebuilt on every render; key on content so an unchanged chart
  // is not re-embedded.
  const key = JSON.stringify(request);
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- `key` is `request`'s content
  const stable = React.useMemo(() => request, [key]);
  const placeholder = (
    <Skeleton aria-busy="true" style={{ height }} className="w-full" />
  );
  return (
    <div ref={setRoot} className="min-w-0" style={{ minHeight: height }}>
      {root ? (
        <React.Suspense fallback={placeholder}>
          <ChartView request={stable} root={root} themeKey={themeCss} />
        </React.Suspense>
      ) : (
        placeholder
      )}
    </div>
  );
}

export function BarChartComponent({ element }: ComponentRenderProps) {
  const props = readProps(APP_COMPONENTS.BarChart.props, element.props);
  return <ChartHost request={{ name: "BarChart", props }} />;
}

export function LineChartComponent({ element }: ComponentRenderProps) {
  const props = readProps(APP_COMPONENTS.LineChart.props, element.props);
  return <ChartHost request={{ name: "LineChart", props }} />;
}

export function AreaChartComponent({ element }: ComponentRenderProps) {
  const props = readProps(APP_COMPONENTS.AreaChart.props, element.props);
  return <ChartHost request={{ name: "AreaChart", props }} />;
}

export function PieChartComponent({ element }: ComponentRenderProps) {
  const props = readProps(APP_COMPONENTS.PieChart.props, element.props);
  return <ChartHost request={{ name: "PieChart", props }} />;
}

export function ScatterChartComponent({ element }: ComponentRenderProps) {
  const props = readProps(APP_COMPONENTS.ScatterChart.props, element.props);
  return <ChartHost request={{ name: "ScatterChart", props }} />;
}

export function CustomChartComponent({ element }: ComponentRenderProps) {
  const props = readProps(APP_COMPONENTS.Chart.props, element.props);
  // readProps drops a spec that fails the schema; a streaming (absent) spec
  // stays quiet, but one that was supplied and is invalid must say why.
  const raw: unknown = element.props;
  const supplied =
    typeof raw === "object" && raw !== null && "spec" in raw
      ? (raw as { spec?: unknown }).spec
      : undefined;
  const check =
    supplied === undefined
      ? null
      : APP_COMPONENTS.Chart.props.shape.spec.safeParse(supplied);
  const specErrors =
    check && !check.success ? check.error.issues.map((i) => i.message) : [];
  return (
    <ChartHost
      request={{
        name: "Chart",
        props,
        ...(specErrors.length > 0 ? { specErrors } : {}),
      }}
    />
  );
}
